'use client';

import { useEffect } from 'react';

/**
 * WebMCP: the page offering its own actions to a browser agent as callable tools. An origin-trial
 * API, so `document.modelContext` is undefined for almost every visitor and this is a no-op by
 * design. See the WebMCP entry in this app's CLAUDE.md.
 */
export type WebMcpTool = {
  name: string;
  description: string;
  /** JSON Schema. `webmcp-schema-validity` fails the page if this does not describe the input. */
  inputSchema: {
    type: 'object';
    properties: Record<string, { type: string; description: string; enum?: string[] }>;
    required?: string[];
  };
  /** `input` is checked against `inputSchema` by the caller, not by TypeScript. */
  execute: (input: never, options: { signal: AbortSignal }) => Promise<string>;
  annotations?: { readOnlyHint?: boolean };
};

declare global {
  interface Document {
    modelContext?: {
      /** The trial ships `void` where the spec says a promise, so a caller has to accept both. */
      registerTool: (tool: WebMcpTool, options: { signal: AbortSignal }) => Promise<void> | void;
    };
  }
}

/**
 * Registers without letting the trial API reach React. Two shapes get past a bare `.catch`: a
 * `void` return, where `.catch` is read off `undefined`, and a duplicate name, which throws
 * synchronously. Either one escaping this effect trips the route's `error.tsx` and replaces the
 * whole page with the error screen — so a visitor loses the site over an enhancement they never
 * asked for. The page is correct without the tool; it is never correct without the page.
 *
 * WORKAROUND: both deviations are the browser's, so there is nothing to fix at source. Drop this
 * once the shipped API returns a promise and stops throwing on a duplicate name — or sooner, with
 * the rest of the WebMCP surface when the origin trial ends.
 */
export function offerTool({
  modelContext,
  tool,
  signal,
}: {
  modelContext: NonNullable<Document['modelContext']>;
  tool: WebMcpTool;
  signal: AbortSignal;
}): void {
  try {
    // A rejection here means the page was torn down mid-registration, which is not worth reporting.
    void Promise.resolve(modelContext.registerTool(tool, { signal })).catch(() => {});
  } catch {
    // Registration refused. Nothing to retry and nothing to tell the visitor.
  }
}

/**
 * Offers a tool while the component offering it is mounted. Aborting is idempotent, so Strict
 * Mode's double-invoked effect cannot leave a duplicate registered.
 */
export function useWebMcpTool(tool: WebMcpTool) {
  useEffect(() => {
    const modelContext = document.modelContext;
    if (!modelContext) return;
    const controller = new AbortController();
    offerTool({ modelContext, tool, signal: controller.signal });
    return () => controller.abort();
  }, [tool]);
}
